import React, { useState, useEffect } from "react";
import { apiClient } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  Plus, 
  Edit2, 
  Trash2, 
  Play,
  Pause,
  Users,
  Mail,
  Clock,
  ChevronRight,
  Zap,
  Settings2,
  BarChart3,
  Eye,
  MousePointer,
  UserPlus,
  UserMinus,
  Search,
  X,
  CheckCircle,
  Loader2
} from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";

export const SequencesPage = () => {
  const [sequences, setSequences] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingSequence, setEditingSequence] = useState(null);
  const [sequenceToDelete, setSequenceToDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  // Enrollment management state
  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false);
  const [enrollingSequence, setEnrollingSequence] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [loadingEnrollments, setLoadingEnrollments] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState(new Set());
  const [contactSearch, setContactSearch] = useState("");
  const [enrolling, setEnrolling] = useState(false);

  // Analytics state
  const [analyticsDialogOpen, setAnalyticsDialogOpen] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    steps: []
  });

  useEffect(() => {
    fetchSequences();
    fetchTemplates();
    fetchContacts();
  }, []);

  const fetchSequences = async () => {
    try {
      const response = await apiClient.get("/sequences");
      setSequences(response.data);
    } catch (error) {
      toast.error("Failed to fetch sequences");
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await apiClient.get("/templates");
      setTemplates(response.data);
    } catch (error) {
      console.error("Failed to fetch templates");
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await apiClient.get("/contacts?page_size=1000");
      // Filter to only contacts with email
      const contactsWithEmail = (response.data.contacts || response.data || []).filter(
        c => c.email && c.email.trim() !== "" && !c.unsubscribed
      );
      setContacts(contactsWithEmail);
    } catch (error) {
      console.error("Failed to fetch contacts");
    }
  };

  const openCreateDialog = () => {
    setEditingSequence(null);
    setFormData({ name: "", description: "", steps: [] });
    setDialogOpen(true);
  };

  const openEditDialog = (sequence) => {
    setEditingSequence(sequence);
    setFormData({
      name: sequence.name,
      description: sequence.description || "",
      steps: sequence.steps || []
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name) {
      toast.error("Please enter a sequence name");
      return;
    }

    setSaving(true);
    try {
      if (editingSequence) {
        await apiClient.put(`/sequences/${editingSequence.id}`, formData);
        toast.success("Sequence updated");
      } else {
        await apiClient.post("/sequences", formData);
        toast.success("Sequence created");
      }
      setDialogOpen(false);
      fetchSequences();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save sequence");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!sequenceToDelete) return;
    try {
      await apiClient.delete(`/sequences/${sequenceToDelete}`);
      toast.success("Sequence deleted");
      fetchSequences();
    } catch (error) {
      toast.error("Failed to delete sequence");
    }
    setDeleteDialogOpen(false);
    setSequenceToDelete(null);
  };

  const handleActivate = async (sequenceId) => {
    try {
      await apiClient.post(`/sequences/${sequenceId}/activate`);
      toast.success("Sequence activated");
      fetchSequences();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to activate sequence");
    }
  };

  const handlePause = async (sequenceId) => {
    try {
      await apiClient.post(`/sequences/${sequenceId}/pause`);
      toast.success("Sequence paused");
      fetchSequences();
    } catch (error) {
      toast.error("Failed to pause sequence");
    }
  };

  const fetchAnalytics = async (sequenceId) => {
    setLoadingAnalytics(true);
    try {
      const response = await apiClient.get(`/sequences/${sequenceId}/analytics`);
      setAnalyticsData(response.data);
      setAnalyticsDialogOpen(true);
    } catch (error) {
      toast.error("Failed to fetch analytics");
    } finally {
      setLoadingAnalytics(false);
    }
  };

  // Enrollment Management Functions
  const openEnrollDialog = async (sequence) => {
    setEnrollingSequence(sequence);
    setSelectedContacts(new Set());
    setContactSearch("");
    setEnrollDialogOpen(true);
    await fetchEnrollments(sequence.id);
  };

  const fetchEnrollments = async (sequenceId) => {
    setLoadingEnrollments(true);
    try {
      const response = await apiClient.get(`/sequences/${sequenceId}/enrollments`);
      setEnrollments(response.data);
    } catch (error) {
      toast.error("Failed to fetch enrollments");
    } finally {
      setLoadingEnrollments(false);
    }
  };

  const handleEnrollContacts = async () => {
    if (!enrollingSequence || selectedContacts.size === 0) return;
    
    setEnrolling(true);
    try {
      const response = await apiClient.post(`/sequences/${enrollingSequence.id}/enroll`, {
        contact_ids: Array.from(selectedContacts)
      });
      toast.success(response.data.message || `Enrolled ${response.data.enrolled} contacts`);
      setSelectedContacts(new Set());
      await fetchEnrollments(enrollingSequence.id);
      fetchSequences(); // Refresh sequence counts
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to enroll contacts");
    } finally {
      setEnrolling(false);
    }
  };

  const handleRemoveEnrollment = async (enrollmentId) => {
    if (!enrollingSequence) return;
    
    try {
      await apiClient.delete(`/sequences/${enrollingSequence.id}/enrollments/${enrollmentId}`);
      toast.success("Contact removed from sequence");
      await fetchEnrollments(enrollingSequence.id);
      fetchSequences(); // Refresh sequence counts
    } catch (error) {
      toast.error("Failed to remove contact");
    }
  };

  const toggleContactSelection = (contactId) => {
    setSelectedContacts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(contactId)) {
        newSet.delete(contactId);
      } else {
        newSet.add(contactId);
      }
      return newSet;
    });
  };

  // Filter contacts for enrollment (not already enrolled and matching search)
  const getAvailableContacts = () => {
    const enrolledContactIds = new Set(enrollments.map(e => e.contact_id));
    return contacts.filter(c => {
      const notEnrolled = !enrolledContactIds.has(c.id);
      const matchesSearch = !contactSearch || 
        c.email?.toLowerCase().includes(contactSearch.toLowerCase()) ||
        c.name?.toLowerCase().includes(contactSearch.toLowerCase()) ||
        c.business_name?.toLowerCase().includes(contactSearch.toLowerCase());
      return notEnrolled && matchesSearch;
    });
  };

  const addStep = () => {
    setFormData(prev => ({
      ...prev,
      steps: [
        ...prev.steps,
        {
          template_id: "",
          delay_days: prev.steps.length === 0 ? 0 : 1,
          delay_hours: 0,
          subject_override: ""
        }
      ]
    }));
  };

  const updateStep = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.map((step, i) => 
        i === index ? { ...step, [field]: value } : step
      )
    }));
  };

  const removeStep = (index) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index)
    }));
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: "bg-slate-100 text-slate-700",
      active: "bg-emerald-100 text-emerald-700",
      paused: "bg-amber-100 text-amber-700"
    };
    return (
      <Badge className={styles[status] || styles.draft}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const getTemplateName = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    return template ? template.name : "Select template";
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="sequences-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading tracking-tight text-slate-900">
            Email Sequences
          </h1>
          <p className="text-slate-500 mt-1">
            Create automated drip campaigns to nurture your leads
          </p>
        </div>
        <Button
          onClick={openCreateDialog}
          className="bg-slate-900 hover:bg-slate-800"
          data-testid="create-sequence-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Sequence
        </Button>
      </div>

      {/* Info Card */}
      <Card className="border-slate-200 bg-gradient-to-r from-sky-50 to-indigo-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center">
              <Zap className="w-5 h-5 text-sky-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">How Sequences Work</h3>
              <p className="text-sm text-slate-600 mt-1">
                Create a series of emails sent automatically over time. 
                Example: Day 0 → Welcome email, Day 3 → Follow-up, Day 7 → Special offer.
                Enroll contacts and they'll receive each email on schedule.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sequences Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="w-8 h-8 border-2 border-slate-200 border-t-slate-800 rounded-full animate-spin" />
        </div>
      ) : sequences.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Mail className="w-12 h-12 text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No sequences yet</h3>
            <p className="text-slate-500 text-center max-w-md mb-4">
              Create your first email sequence to automate your outreach
            </p>
            <Button onClick={openCreateDialog} className="bg-slate-900 hover:bg-slate-800">
              <Plus className="w-4 h-4 mr-2" />
              Create Sequence
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sequences.map((sequence) => (
            <Card 
              key={sequence.id} 
              className="border-slate-200 hover:shadow-md transition-shadow"
              data-testid={`sequence-card-${sequence.id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="font-heading text-lg truncate">
                      {sequence.name}
                    </CardTitle>
                    {sequence.description && (
                      <CardDescription className="mt-1 line-clamp-2">
                        {sequence.description}
                      </CardDescription>
                    )}
                  </div>
                  {getStatusBadge(sequence.status)}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {/* Stats */}
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1.5 text-slate-600">
                      <Mail className="w-4 h-4" />
                      <span>{sequence.steps?.length || 0} steps</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-emerald-600">
                      <Users className="w-4 h-4" />
                      <span>{sequence.active_enrollments || 0} active</span>
                    </div>
                  </div>

                  {/* Steps Preview */}
                  {sequence.steps && sequence.steps.length > 0 && (
                    <div className="flex items-center gap-1 overflow-x-auto pb-1">
                      {sequence.steps.slice(0, 4).map((step, idx) => (
                        <React.Fragment key={idx}>
                          <div className="flex-shrink-0 px-2 py-1 bg-slate-100 rounded text-xs font-medium text-slate-600">
                            {step.delay_days === 0 && step.delay_hours === 0 
                              ? "Immediate" 
                              : `Day ${step.delay_days}${step.delay_hours > 0 ? `+${step.delay_hours}h` : ''}`
                            }
                          </div>
                          {idx < sequence.steps.length - 1 && idx < 3 && (
                            <ChevronRight className="w-3 h-3 text-slate-300 flex-shrink-0" />
                          )}
                        </React.Fragment>
                      ))}
                      {sequence.steps.length > 4 && (
                        <span className="text-xs text-slate-400">+{sequence.steps.length - 4} more</span>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                    {sequence.status === "active" ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePause(sequence.id)}
                        className="flex-1"
                      >
                        <Pause className="w-4 h-4 mr-1" />
                        Pause
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleActivate(sequence.id)}
                        className="flex-1 text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                        disabled={!sequence.steps || sequence.steps.length === 0}
                      >
                        <Play className="w-4 h-4 mr-1" />
                        Activate
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEnrollDialog(sequence)}
                      title="Manage Contacts"
                      className="text-blue-600 hover:bg-blue-50"
                    >
                      <Users className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => fetchAnalytics(sequence.id)}
                      disabled={loadingAnalytics}
                      title="View Analytics"
                    >
                      <BarChart3 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(sequence)}
                    >
                      <Settings2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSequenceToDelete(sequence.id);
                        setDeleteDialogOpen(true);
                      }}
                      className="text-slate-400 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingSequence ? "Edit Sequence" : "Create Sequence"}
            </DialogTitle>
            <DialogDescription>
              Build an automated email series that sends over time
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Sequence Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Welcome Series"
                  className="mt-1.5"
                  data-testid="sequence-name-input"
                />
              </div>
              <div>
                <Label htmlFor="description">Description (optional)</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the purpose of this sequence..."
                  className="mt-1.5"
                  rows={2}
                />
              </div>
            </div>

            {/* Steps */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label>Email Steps</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addStep}
                  data-testid="add-step-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Step
                </Button>
              </div>

              {formData.steps.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-lg">
                  <Clock className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">No steps yet. Add your first email step.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {formData.steps.map((step, index) => (
                    <Card key={index} className="border-slate-200">
                      <CardContent className="pt-4">
                        <div className="flex items-start gap-4">
                          <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-sm font-bold text-slate-600 flex-shrink-0">
                            {index + 1}
                          </div>
                          <div className="flex-1 space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs">Wait (days)</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  value={step.delay_days}
                                  onChange={(e) => updateStep(index, "delay_days", parseInt(e.target.value) || 0)}
                                  className="mt-1"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">+ Hours</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  max="23"
                                  value={step.delay_hours}
                                  onChange={(e) => updateStep(index, "delay_hours", parseInt(e.target.value) || 0)}
                                  className="mt-1"
                                />
                              </div>
                            </div>
                            <div>
                              <Label className="text-xs">Email Template</Label>
                              <Select
                                value={step.template_id}
                                onValueChange={(value) => updateStep(index, "template_id", value)}
                              >
                                <SelectTrigger className="mt-1">
                                  <SelectValue placeholder="Select template" />
                                </SelectTrigger>
                                <SelectContent>
                                  {templates.map((template) => (
                                    <SelectItem key={template.id} value={template.id}>
                                      {template.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-xs">Subject Override (optional)</Label>
                              <Input
                                value={step.subject_override || ""}
                                onChange={(e) => updateStep(index, "subject_override", e.target.value)}
                                placeholder="Leave blank to use template subject"
                                className="mt-1"
                              />
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeStep(index)}
                            className="text-slate-400 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="save-sequence-btn"
            >
              {saving ? "Saving..." : editingSequence ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Sequence</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this sequence? This will also remove all enrollments. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-sequence-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Analytics Dialog */}
      <Dialog open={analyticsDialogOpen} onOpenChange={setAnalyticsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              Sequence Analytics
            </DialogTitle>
            {analyticsData && (
              <DialogDescription>
                Performance data for "{analyticsData.sequence_name}"
              </DialogDescription>
            )}
          </DialogHeader>

          {analyticsData && (
            <div className="space-y-6 py-4">
              {/* Overall Stats */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="border-slate-200">
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold text-slate-900">{analyticsData.totals.sent}</div>
                    <div className="text-sm text-slate-500">Emails Sent</div>
                  </CardContent>
                </Card>
                <Card className="border-slate-200">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2">
                      <Eye className="w-5 h-5 text-emerald-500" />
                      <span className="text-2xl font-bold text-emerald-600">{analyticsData.totals.open_rate}%</span>
                    </div>
                    <div className="text-sm text-slate-500">Open Rate ({analyticsData.totals.opened} opened)</div>
                  </CardContent>
                </Card>
                <Card className="border-slate-200">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2">
                      <MousePointer className="w-5 h-5 text-blue-500" />
                      <span className="text-2xl font-bold text-blue-600">{analyticsData.totals.click_rate}%</span>
                    </div>
                    <div className="text-sm text-slate-500">Click Rate ({analyticsData.totals.clicked} clicked)</div>
                  </CardContent>
                </Card>
              </div>

              {/* Per-Step Analytics */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Per-Step Performance</h3>
                <div className="space-y-3">
                  {analyticsData.step_analytics.map((step) => {
                    const template = templates.find(t => t.id === step.template_id);
                    return (
                      <div key={step.step_number} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-sm font-bold text-blue-600">
                              {step.step_number}
                            </div>
                            <div>
                              <div className="font-medium text-slate-900">
                                {template?.name || "Unknown Template"}
                              </div>
                              <div className="text-xs text-slate-500">
                                {step.delay_days === 0 && step.delay_hours === 0 
                                  ? "Sent immediately" 
                                  : `Wait ${step.delay_days} day${step.delay_days !== 1 ? 's' : ''}${step.delay_hours > 0 ? ` ${step.delay_hours}h` : ''}`
                                }
                              </div>
                            </div>
                          </div>
                          <Badge variant="outline" className="text-slate-600">
                            {step.sent} sent
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="flex items-center gap-2">
                            <div className="w-full bg-slate-200 rounded-full h-2">
                              <div 
                                className="bg-emerald-500 h-2 rounded-full transition-all"
                                style={{ width: `${step.open_rate}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium text-emerald-600 w-14">{step.open_rate}%</span>
                            <span className="text-xs text-slate-400">opens</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-full bg-slate-200 rounded-full h-2">
                              <div 
                                className="bg-blue-500 h-2 rounded-full transition-all"
                                style={{ width: `${step.click_rate}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium text-blue-600 w-14">{step.click_rate}%</span>
                            <span className="text-xs text-slate-400">clicks</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Enrollment Stats */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <h3 className="text-sm font-semibold text-slate-700 mb-2">Enrollment Status</h3>
                <div className="flex items-center gap-6 text-sm">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-emerald-500" />
                    <span className="text-slate-600">{analyticsData.enrollments.active} active</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-blue-500" />
                    <span className="text-slate-600">{analyticsData.enrollments.completed} completed</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-amber-500" />
                    <span className="text-slate-600">{analyticsData.enrollments.paused} paused</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setAnalyticsDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enrollment Management Dialog */}
      <Dialog open={enrollDialogOpen} onOpenChange={setEnrollDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Manage Contacts
            </DialogTitle>
            {enrollingSequence && (
              <DialogDescription>
                Add or remove contacts from "{enrollingSequence.name}"
                {enrollingSequence.status !== "active" && (
                  <span className="text-amber-600 font-medium ml-2">
                    (Sequence must be active to send emails)
                  </span>
                )}
              </DialogDescription>
            )}
          </DialogHeader>

          <div className="flex-1 overflow-hidden flex flex-col gap-4 py-4">
            {/* Currently Enrolled Section */}
            <div className="flex-1 min-h-0 flex flex-col">
              <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                Currently Enrolled ({enrollments.length})
              </h3>
              
              {loadingEnrollments ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : enrollments.length === 0 ? (
                <div className="text-center py-6 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
                  <Users className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">No contacts enrolled yet</p>
                </div>
              ) : (
                <div className="flex-1 overflow-y-auto border rounded-lg divide-y max-h-48">
                  {enrollments.map((enrollment) => (
                    <div
                      key={enrollment.id}
                      className="flex items-center justify-between px-3 py-2 hover:bg-slate-50"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {enrollment.contact?.email || "Unknown"}
                        </p>
                        <p className="text-xs text-slate-500 truncate">
                          {enrollment.contact?.name || enrollment.contact?.business_name || ""}
                          <span className="mx-1">•</span>
                          Step {enrollment.current_step} of {enrollingSequence?.steps?.length || "?"}
                          <span className="mx-1">•</span>
                          <Badge variant="outline" className="text-xs py-0 px-1">
                            {enrollment.status}
                          </Badge>
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveEnrollment(enrollment.id)}
                        className="text-slate-400 hover:text-red-600 flex-shrink-0"
                        title="Remove from sequence"
                      >
                        <UserMinus className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add Contacts Section */}
            <div className="flex-1 min-h-0 flex flex-col border-t pt-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                <UserPlus className="w-4 h-4 text-blue-500" />
                Add Contacts to Sequence
              </h3>

              {/* Search */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={contactSearch}
                  onChange={(e) => setContactSearch(e.target.value)}
                  placeholder="Search contacts by email, name, or business..."
                  className="pl-9"
                />
                {contactSearch && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                    onClick={() => setContactSearch("")}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>

              {/* Contact List */}
              <div className="flex-1 overflow-y-auto border rounded-lg max-h-48">
                {getAvailableContacts().length === 0 ? (
                  <div className="text-center py-6 text-sm text-slate-500">
                    {contacts.length === 0 
                      ? "No contacts available" 
                      : contactSearch 
                        ? "No matching contacts found"
                        : "All contacts are already enrolled"
                    }
                  </div>
                ) : (
                  <div className="divide-y">
                    {getAvailableContacts().slice(0, 50).map((contact) => (
                      <label
                        key={contact.id}
                        className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer"
                      >
                        <Checkbox
                          checked={selectedContacts.has(contact.id)}
                          onCheckedChange={() => toggleContactSelection(contact.id)}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">
                            {contact.email}
                          </p>
                          <p className="text-xs text-slate-500 truncate">
                            {contact.name || contact.business_name || contact.location || ""}
                          </p>
                        </div>
                      </label>
                    ))}
                    {getAvailableContacts().length > 50 && (
                      <p className="text-center text-xs text-slate-400 py-2">
                        Showing 50 of {getAvailableContacts().length} contacts. Use search to find more.
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Selection summary */}
              {selectedContacts.size > 0 && (
                <div className="mt-2 flex items-center justify-between bg-blue-50 rounded-lg px-3 py-2">
                  <span className="text-sm text-blue-700">
                    {selectedContacts.size} contact{selectedContacts.size !== 1 ? "s" : ""} selected
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedContacts(new Set())}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    Clear selection
                  </Button>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setEnrollDialogOpen(false)}>
              Close
            </Button>
            <Button
              onClick={handleEnrollContacts}
              disabled={selectedContacts.size === 0 || enrolling}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {enrolling ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Enrolling...
                </span>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Enroll {selectedContacts.size > 0 ? selectedContacts.size : ""} Contact{selectedContacts.size !== 1 ? "s" : ""}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SequencesPage;
